// ======================== OFFLINE MODE HOOK ========================
// Gestion du mode hors-ligne pour la caisse POS
// Stockage local avec IndexedDB et synchronisation automatique

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

// IndexedDB database name and store names
const DB_NAME = 'aldrins_pos_offline';
const DB_VERSION = 1;
const STORES = {
    PRODUCTS: 'products',
    CATEGORIES: 'categories',
    PENDING_ORDERS: 'pending_orders',
    CART: 'cart',
    SETTINGS: 'settings'
};

// Open IndexedDB database
const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            
            // Create stores if they don't exist
            if (!db.objectStoreNames.contains(STORES.PRODUCTS)) {
                db.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORES.CATEGORIES)) {
                db.createObjectStore(STORES.CATEGORIES, { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
                const store = db.createObjectStore(STORES.PENDING_ORDERS, { keyPath: 'local_id', autoIncrement: true });
                store.createIndex('created_at', 'created_at');
            }
            if (!db.objectStoreNames.contains(STORES.CART)) {
                db.createObjectStore(STORES.CART, { keyPath: 'session_id' });
            }
            if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
                db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
            }
        };
    });
};

// Generic store operations
const getAll = async (storeName) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const putItem = async (storeName, item) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.put(item);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const deleteItem = async (storeName, key) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

const clearStore = async (storeName) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

const bulkPut = async (storeName, items) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        items.forEach(item => store.put(item));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
};

export const useOfflineMode = (api) => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [offlineProducts, setOfflineProducts] = useState([]);
    const [offlineCategories, setOfflineCategories] = useState([]);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const syncIntervalRef = useRef(null);

    // Update online status
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            toast.success('Connexion rétablie', { duration: 3000 });
            // Auto-sync when back online
            syncPendingOrders();
        };
        
        const handleOffline = () => {
            setIsOnline(false);
            toast.warning('Mode hors-ligne activé', { 
                description: 'Les commandes seront enregistrées localement',
                duration: 5000 
            });
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Load pending orders count on mount
    useEffect(() => {
        loadPendingOrdersCount();
        loadOfflineData();
    }, []);

    // Periodic sync when online
    useEffect(() => {
        if (isOnline) {
            syncIntervalRef.current = setInterval(() => {
                syncPendingOrders();
            }, 30000); // Sync every 30 seconds
        } else {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
            }
        }
        return () => {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
            }
        };
    }, [isOnline]);

    const loadPendingOrdersCount = async () => {
        try {
            const orders = await getAll(STORES.PENDING_ORDERS);
            setPendingOrdersCount(orders.length);
        } catch (error) {
            console.error('Error loading pending orders count:', error);
        }
    };

    const loadOfflineData = async () => {
        try {
            const [products, categories] = await Promise.all([
                getAll(STORES.PRODUCTS),
                getAll(STORES.CATEGORIES)
            ]);
            setOfflineProducts(products);
            setOfflineCategories(categories);
            
            // Get last sync time
            const db = await openDB();
            const tx = db.transaction(STORES.SETTINGS, 'readonly');
            const store = tx.objectStore(STORES.SETTINGS);
            const request = store.get('lastSync');
            request.onsuccess = () => {
                if (request.result) {
                    setLastSyncTime(new Date(request.result.value));
                }
            };
        } catch (error) {
            console.error('Error loading offline data:', error);
        }
    };

    // Cache products and categories for offline use
    const cacheProductsAndCategories = useCallback(async (products, categories) => {
        try {
            await Promise.all([
                bulkPut(STORES.PRODUCTS, products),
                bulkPut(STORES.CATEGORIES, categories)
            ]);
            
            // Update last sync time
            await putItem(STORES.SETTINGS, { key: 'lastSync', value: new Date().toISOString() });
            setLastSyncTime(new Date());
            
            setOfflineProducts(products);
            setOfflineCategories(categories);
            
            console.log('[Offline] Cached', products.length, 'products and', categories.length, 'categories');
        } catch (error) {
            console.error('Error caching data:', error);
        }
    }, []);

    // Save order locally when offline
    const saveOrderLocally = useCallback(async (orderData) => {
        try {
            const localOrder = {
                ...orderData,
                local_id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                created_at: new Date().toISOString(),
                synced: false,
                offline: true
            };
            
            await putItem(STORES.PENDING_ORDERS, localOrder);
            await loadPendingOrdersCount();
            
            toast.success('Commande enregistrée localement', {
                description: 'Elle sera synchronisée dès que la connexion sera rétablie'
            });
            
            return localOrder;
        } catch (error) {
            console.error('Error saving order locally:', error);
            toast.error('Erreur lors de l\'enregistrement local');
            throw error;
        }
    }, []);

    // Get pending orders
    const getPendingOrders = useCallback(async () => {
        try {
            return await getAll(STORES.PENDING_ORDERS);
        } catch (error) {
            console.error('Error getting pending orders:', error);
            return [];
        }
    }, []);

    // Sync pending orders to server
    const syncPendingOrders = useCallback(async () => {
        if (!isOnline || isSyncing) return;
        
        setIsSyncing(true);
        
        try {
            const pendingOrders = await getAll(STORES.PENDING_ORDERS);
            
            if (pendingOrders.length === 0) {
                setIsSyncing(false);
                return;
            }
            
            let syncedCount = 0;
            let failedCount = 0;
            
            for (const order of pendingOrders) {
                try {
                    // Create order on server
                    const response = await api().post('/pos/orders', {
                        order_type: order.order_type,
                        table_number: order.table_number,
                        table_id: order.table_id,
                        customer_name: order.customer_name,
                        items: order.items,
                        notes: order.notes,
                        register_id: order.register_id,
                        discount_percent: order.discount_percent,
                        offline_id: order.local_id // Send offline ID for tracking
                    });
                    
                    // If order was pre-paid offline, process payment
                    if (order.payment) {
                        await api().post(`/pos/orders/${response.data.id}/pay`, order.payment);
                    }
                    
                    // Remove from local storage
                    await deleteItem(STORES.PENDING_ORDERS, order.local_id);
                    syncedCount++;
                    
                } catch (error) {
                    console.error('Error syncing order:', order.local_id, error);
                    failedCount++;
                }
            }
            
            await loadPendingOrdersCount();
            
            if (syncedCount > 0) {
                toast.success(`${syncedCount} commande(s) synchronisée(s)`);
            }
            if (failedCount > 0) {
                toast.error(`${failedCount} commande(s) en échec de synchronisation`);
            }
            
        } catch (error) {
            console.error('Error during sync:', error);
        } finally {
            setIsSyncing(false);
        }
    }, [api, isOnline, isSyncing]);

    // Save cart for session recovery
    const saveCartOffline = useCallback(async (sessionId, cart, metadata = {}) => {
        try {
            await putItem(STORES.CART, {
                session_id: sessionId,
                cart,
                metadata,
                updated_at: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error saving cart offline:', error);
        }
    }, []);

    // Recover cart from offline storage
    const recoverCart = useCallback(async (sessionId) => {
        try {
            const db = await openDB();
            const tx = db.transaction(STORES.CART, 'readonly');
            const store = tx.objectStore(STORES.CART);
            const request = store.get(sessionId);
            
            return new Promise((resolve, reject) => {
                request.onsuccess = () => resolve(request.result);
                request.onerror = () => reject(request.error);
            });
        } catch (error) {
            console.error('Error recovering cart:', error);
            return null;
        }
    }, []);

    // Clear offline data
    const clearOfflineData = useCallback(async () => {
        try {
            await Promise.all([
                clearStore(STORES.PRODUCTS),
                clearStore(STORES.CATEGORIES),
                clearStore(STORES.CART)
            ]);
            setOfflineProducts([]);
            setOfflineCategories([]);
            toast.success('Données hors-ligne effacées');
        } catch (error) {
            console.error('Error clearing offline data:', error);
        }
    }, []);

    // Delete a specific pending order
    const deletePendingOrder = useCallback(async (localId) => {
        try {
            await deleteItem(STORES.PENDING_ORDERS, localId);
            await loadPendingOrdersCount();
            toast.success('Commande supprimée');
        } catch (error) {
            console.error('Error deleting pending order:', error);
        }
    }, []);

    return {
        isOnline,
        isOffline: !isOnline,
        pendingOrdersCount,
        isSyncing,
        offlineProducts,
        offlineCategories,
        lastSyncTime,
        // Methods
        cacheProductsAndCategories,
        saveOrderLocally,
        getPendingOrders,
        syncPendingOrders,
        saveCartOffline,
        recoverCart,
        clearOfflineData,
        deletePendingOrder
    };
};

export default useOfflineMode;
